import React, { useState } from 'react';
import { IonPage, IonContent } from '@ionic/react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Mail, Phone, MapPin, Globe, Plus, Minus } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import styles from './Contact.module.scss';

interface FormData {
  name: string;
  email: string;
  company: string;
  service: string;
  budget: string;
  message: string;
}

const contactInfo = [
  { icon: <Mail size={16} />, label: 'EMAIL', value: 'arkatechsitsolutions@gmail.com', href: 'mailto:arkatechsitsolutions@gmail.com' },
  { icon: <Phone size={16} />, label: 'PHONE', value: '+91 70215 11094', href: 'tel:+917021511094' },
  { icon: <MapPin size={16} />, label: 'LOCATION', value: 'Bhiwandi, Maharashtra, India', href: null },
  { icon: <Globe size={16} />, label: 'OPERATING REGION', value: 'Worldwide · Remote-First', href: null },
];

const socialLinks = [
  { icon: '📸', label: 'Instagram', href: 'https://www.instagram.com/arkatechs/' },
];

const faqs = [
  { q: 'What services does Arkatechs provide?', a: 'We offer web development, mobile app development, UI/UX design, AI & automation, cloud solutions, digital marketing, SEO, hosting, brand identity, and UGC advertisements.' },
  { q: 'Can you build custom software?', a: 'Yes. We build modern custom websites, web applications, and mobile applications tailored to your specific business needs.' },
  { q: 'Who does Arkatechs work with?', a: 'We work with startups, small businesses, medium-sized businesses, established companies, SaaS companies, e-commerce businesses, local businesses, creators, personal brands, entrepreneurs, and organizations launching new digital products.' },
  { q: 'Where is Arkatechs based?', a: 'Arkatechs is based in India and works with businesses worldwide. We operate as a remote-first company.' },
  { q: 'How do we get started?', a: 'Reach out using the form above or contact us directly. We will discuss your goals and project requirements to explore how we can help.' },
];

const Contact: React.FC = () => {
  const [form, setForm] = useState<FormData>({
    name: '', email: '', company: '', service: '', budget: '', message: '',
  });
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Static demo
  };

  return (
    <IonPage id="contact-page">
      <IonContent fullscreen scrollY scrollEvents={true}>
        <div className="page-wrapper">
          <Navbar />

          {/* ── Hero ────────────────────────────────────────────── */}
          <section className={styles.hero}>
            <div className={styles.heroDecor}>
              <div className="concentric-circles" style={{ top: '0', right: '-10%', left: 'auto', transform: 'none' }} />
              <div className="dot-grid" style={{ top: 'auto', bottom: '0', left: '50%', right: '0' }} />
              <div className="gradient-line" style={{ position: 'absolute', top: '50%', right: '0', width: '30%' }} />
            </div>

            <div className={`container ${styles.heroContainer}`}>
              <motion.div
                className={styles.heroContent}
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7 }}
              >
                <span className={styles.eyebrow}>CONTACT US</span>
                <h1 className={styles.heroTitle}>
                  LET'S BUILD<br />
                  <span className={styles.gradientText}>SOMETHING MEANINGFUL.</span>
                </h1>
                <p className={styles.heroSub}>
                  Tell us about your idea, project, or business challenge. We'll explore how Arkatechs can help turn it into a digital solution.
                </p>
                <a href="#contact-form" className={styles.heroCta}>
                  LET'S TALK <ArrowRight size={16} />
                </a>
              </motion.div>
            </div>
            
            <div className="torn-edge-bottom" />
          </section>

          {/* ── Contact Area ────────────────────────────────────── */}
          <section className={styles.contactSection} id="contact-form">
            <div className={`container ${styles.contactGrid}`}>
              {/* Left Column */}
              <motion.div
                className={styles.contactLeft}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className={styles.contactHeader}>
                  <span className={styles.eyebrow}>GET IN TOUCH</span>
                  <h2 className={styles.contactTitle}>WE'D LOVE<br />TO HEAR<br />FROM YOU.</h2>
                </div>

                <div className={styles.contactInfoList}>
                  {contactInfo.map((item) => (
                    <div key={item.label} className={styles.infoItem}>
                      <div className={styles.infoLabel}>{item.label}</div>
                      {item.href ? (
                        <a href={item.href} className={styles.infoValue}>
                          {item.icon} {item.value}
                        </a>
                      ) : (
                        <div className={styles.infoValue}>
                          {item.icon} {item.value}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className={styles.socialSection}>
                  <div className={styles.infoLabel}>FOLLOW US</div>
                  <div className={styles.socialIcons}>
                    {socialLinks.map((s) => (
                      <a key={s.label} href={s.href} className={styles.socialBtn} aria-label={s.label} target="_blank" rel="noopener noreferrer">
                        <span style={{ fontSize: '18px' }}>{s.icon}</span>
                      </a>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* Separator */}
              <div className={styles.separator}>
                <div className={styles.separatorDot} />
              </div>

              {/* Right Column / Form */}
              <motion.div
                className={styles.contactRight}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <form className={styles.form} onSubmit={handleSubmit}>
                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label>FULL NAME *</label>
                      <input type="text" name="name" required value={form.name} onChange={handleChange} />
                    </div>
                    <div className={styles.formGroup}>
                      <label>EMAIL ADDRESS *</label>
                      <input type="email" name="email" required value={form.email} onChange={handleChange} />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label>COMPANY</label>
                      <input type="text" name="company" value={form.company} onChange={handleChange} />
                    </div>
                    <div className={styles.formGroup}>
                      <label>SERVICE YOU NEED *</label>
                      <select name="service" required value={form.service} onChange={handleChange}>
                        <option value="" disabled>Select a service</option>
                        <option value="web">Web Development</option>
                        <option value="mobile">Mobile App Development</option>
                        <option value="design">UI/UX Design</option>
                        <option value="ai">AI & Automation</option>
                        <option value="cloud">Cloud Solutions</option>
                        <option value="marketing">Digital Marketing</option>
                        <option value="seo">SEO</option>
                        <option value="hosting">Hosting</option>
                        <option value="brand">Logo & Brand Identity</option>
                        <option value="ugc">UGC Advertisements</option>
                      </select>
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <label>TELL US ABOUT YOUR PROJECT *</label>
                    <textarea name="message" required rows={4} value={form.message} onChange={handleChange} />
                  </div>

                  <div className={styles.formActions}>
                    <button type="submit" className={styles.submitBtn}>
                      SEND MESSAGE <ArrowRight size={18} />
                    </button>
                    <p className={styles.secureText}>
                      Your information is secure and will never be shared.
                    </p>
                  </div>
                </form>
              </motion.div>
            </div>
            
            <div className="torn-edge-bottom" style={{ zIndex: 3 }} />
          </section>

          {/* ── FAQ Section ─────────────────────────────────────── */}
          <section className={styles.faqSection}>
            <div className={`container ${styles.faqGrid}`}>
              <motion.div
                className={styles.faqLeft}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className={styles.faqTitle}>
                  FREQUENTLY<br />ASKED<br />QUESTIONS.
                </h2>
              </motion.div>

              <div className={styles.faqRight}>
                {faqs.map((faq, index) => (
                  <motion.div
                    key={index}
                    className={styles.faqItem}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <button
                      className={styles.faqQuestion}
                      onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                    >
                      <div className={styles.faqQText}>
                        <span className={styles.faqNumber}>0{index + 1}</span>
                        {faq.q}
                      </div>
                      <span className={styles.faqIcon}>
                        {activeFaq === index ? <Minus size={18} /> : <Plus size={18} />}
                      </span>
                    </button>
                    <AnimatePresence>
                      {activeFaq === index && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className={styles.faqAnswer}
                        >
                          <p>{faq.a}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          <Footer />
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Contact;
